import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';

export default class JavierMcBride extends BasePracticeScenario {

	public isUpDown: boolean;
	public isUpDownString: any;
	public isHumidityString: string;
	public isLarenManip: boolean;
	public isLarenManipString: any;
	public isHumidity2String: string;

	public get maxScore(): number {
		return 54600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var preCricPoints = 3900;
		var awardedPoints = 32700;

		return assessPatientRemoveDenturesPoints + awardedPoints +
					setupFiberOpticsPoints + preCricPoints;
    }

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 2;
    }

	public getDefaultApneaTime(): number {
		return 210
	}

	public getDefaultSa02(): number {
		return 95
	}
	
	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);
		
		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				"Intubation is the optimal route for long-term care.",
                "Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?"
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished() : void  {
		if(this.isLarenManip) {
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'You established a stable, patent airway for Javier McBride by making a successful intubation attempt using laryngeal manipulation. Mr. McBride was apneic for ' + this.apneaTimeElapsed + ' while you managed his airway. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points and unlocked a video laryngoscope. Review the debrief to help you prepare for additional scenarios and to see how you can improve your score.', 'Good Job on this Scenario', './assets/images/success/VL.png');
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished() : void  {
		if(this.isUpDown) {
			this.showTempSuccessPopUpAfterAuscultate('This patient is going to be on a ventilator; we need an endotracheal tube.', VortexUICurrentAttempt.SGA);
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData() : AssessAirwayActionData[]  {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'His larynx is free and mobile. You can barely palpate the thyroid and there is about 3 centimeters of fat.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 3 finger breadths (4cm) <br/> Hyoid to Thyroid Notch Distance: 1 finger breadth (2cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'No feedback to display'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient is not wearing dentures',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Patient does not have dentures'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4cm)',
				imgUrl:'./assets/images/assess-airway-images/Class1.png'
			}
		]
    }

	private _setupIntro () {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Good morning. This is Javier McBride. He’s an obese 54-year-old male, 68 in, 150 kg. 
		// 			He suffers from hypertension, coronary artery disease, and diabetes mellitus. 
		// 			He has an ejection fraction of 35%. Patient has a probable pneumonia and is experiencing 
		// 			an acute onset of respiratory distress. His room air saturation was 89%.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario2 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			The patient has an 18 gauge IV in place and is using a 100% non-rebreather mask. 
		// 			His K is 3.8. He needs to be intubated for obvious respiratory distress and management of his pneumonia. 
		// 			I’ll manage the hemodynamics, you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario2 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Javier McBride comes in with probable pneumonia and is experiencing an acute onset of respiratory distress. His room air saturation was 89%.`,
		// 		b: `
		// 			Age: 54<br />
		// 			Height (in): 68 in<br />
		// 			Weight (kg): 150 kg<br />
		// 			Potassium (K): 3.8<br />
		// 			NPO Status: He ate 2 hours ago<br />
		// 			<br />
		// 			PMH: He suffers from hypertension, coronary artery disease, and diabetes mellitus. He has an ejection fraction of 35%
		// 			`,
		// 		a: `He is in respiratory distress due to his pneumonia.`,
		// 		r: `He needs to be intubated for his respiratory distress.`
		// 	}
		// }

		this._id_ScenarioSet(2);

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario2 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Javier McBride comes in with probable pneumonia and is experiencing an acute onset of respiratory distress. His room air saturation was 89%.`,
				b: `
					Age: 54<br />
					Height (in): 68 in<br />
					Weight (kg): 150 kg<br />
					Potassium (K): 3.8<br />
					NPO Status: He ate 2 hours ago<br />
					<br />
					PMH: He suffers from hypertension, coronary artery disease, and diabetes mellitus. He has an ejection fraction of 35%
					`,
				b508: `
					Age. 54.
					Height. 68 inches.
					Weight. 150 kilograms.
					Potassium. 3.8.
					NPO Status. He ate 2 hours ago.
					
					Past Medical History. He suffers from hypertension coronary artery disease and diabetes mellitus. He has an ejection fraction of 35%
					`,	
				a: `He is in respiratory distress due to his pneumonia.`,
				r: `He needs to be intubated for his respiratory distress.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario2 - Flat-rebreather-loop - 54',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			}
		};
		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				//constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario2 - Flat-rebreather-loop - 54',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario2 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj;
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario2 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario2 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario2 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario2 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario2 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection);
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.onMakeAttemptClicked= (isOneHanded: boolean, isOPA: boolean, isNPA: boolean, isManipulateHeadAndNeck: boolean) => {
			if(isOneHanded) {
				this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
			} else {
				this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
			}
		}
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.isLarenManip = false;
		this.procedures.ett.config.isAttemptSuccessful = () => {
			return this.isLarenManip;
		}
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			ettSpecialUIConfig: {
				airwayImage: () => {
					if(this.isLarenManip) {
						return './assets/images/ett/Grade1.png';
					} else return './assets/images/ett/Grade3.png';
				},
				magillForceps: {
					text: () => ''
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => '',
					onClick: () => {
						this.isLarenManip = true;
						if(this.isLarenManip) {
							this.isLarenManipString = this.hoverBreathYes;
							this.isHumidity2String = this.auscSuccess;
						} else {
							this.isLarenManipString = this.hoverBreathNo;
							this.isHumidity2String = this.auscFailed;
						};
					}
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
			}
		};
	}

	private _setupSga() {
		
		this.procedures.sga.config.onUpDownManeuverClickedInOptimizations = () => {
			this.isUpDown = true;
			/*if(this.isUpDown) {
				this.isUpDownString = this.hoverBreathYes;
				this.isHumidityString = this.auscSuccess;
			} else {
				this.isUpDownString = this.hoverBreathNo;
				this.isHumidityString = this.auscFailed;
			};*/
		};

		this.procedures.sga.config.isTempSuccess = () => {
			return this.isUpDown;
		}
		
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
			}
		};
	}

	public setTextAusc() {
		if(this.isLarenManip) {
			return this.auscSuccess;
		}else if(this.isUpDown) {
			return this.auscSuccess;
		} else {
			return this.auscFailed;
		}
	}
	
	public setTextHover() {
		if(this.isLarenManip) {
			return this.hoverBreathYes;
		} else if(this.isUpDown) {
			return this.hoverBreathYes;
		} else {
			return this.hoverBreathNo;
		}
	}

	private _setupAuscultate() {
		/*if(this.isUpDown) {
			this.isUpDownString = this.hoverBreathYes;
			this.isHumidityString = this.auscSuccess;
		} else {
			this.isUpDownString = this.hoverBreathNo;
			this.isHumidityString = this.auscFailed;
		};
		if(this.isLarenManip) {
			this.isLarenManipString = this.hoverBreathYes;
			this.isHumidity2String = this.auscSuccess;
		} else {
			this.isLarenManipString = this.hoverBreathNo;
			this.isHumidity2String = this.auscFailed;
		};*/
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario2/daarc-scenario2-javiermcbride-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathNo
				}
            },
            ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
                checkRiseAndTubeHumidificationText: () => this.setTextAusc(),
				audioPath: () => {
					if(this.isLarenManip) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isLarenManip) {
						return isYesClicked;
					} else return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.setTextHover()
				}
            },
            sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
                checkRiseAndTubeHumidificationText: () => this.setTextAusc(),
				audioPath: () => {
					if(this.isUpDown) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isUpDown) {
						if(isYesClicked){
							this.isUpDown = false // should only toggle if they select yes
						}
						return isYesClicked;
					} else return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.setTextHover()
				}
            },
            cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
                checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathYes
				}
			}
		};
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 140,
			bloodPressureDenominator: 80,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 95,
			heartRate: 90,
			heartRateMax: 200,
			respirationRate: 32,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
            onInductionTimerRanOut: () => {
                context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Javier McBride. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}